/**
 * DeploymentField - Model deployment selector for Agent nodes
 *
 * Fetches available deployments from the agent connection's resourceId
 * and displays them in a combobox that allows both selection and manual entry.
 *
 * For GitHub Copilot connections (no ARM resource), fetches available models
 * from the backend's `listModels` catalog instead.
 */

import { useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Combobox,
  Option,
  Field,
  Button,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useDeploymentOptions, type DeploymentOption } from '@/hooks/useAzureResources';
import { useGhcpModels } from '@/hooks/useGhcpModels';
import { getAgentReferenceProvider } from '@/components/WorkflowDesigner/workflowHelpers';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  combobox: {
    flex: 1,
    minWidth: 0,
  },
  refreshButton: {
    flexShrink: 0,
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  optionSubtext: {
    color: tokens.colorNeutralForeground3,
  },
});

export interface DeploymentFieldProps {
  /** Connection reference name to look up resourceId */
  connectionReferenceName: string | undefined;
  /** Currently selected deployment name */
  value: string;
  /** Callback when selection changes */
  onChange: (deployment: DeploymentOption) => void;
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Validation error message */
  error?: string;
  /** Description/hint text */
  description?: string;
  /**
   * Whether the owning Agent action is a coding agent (GHCP harness). Used to
   * scope the GHCP model-listing behavior — see the `useGhcpModels` call below.
   * Workflow (native) agents are left on their original code path.
   */
  isCodingAgent?: boolean;
}

export const DeploymentField = ({
  connectionReferenceName,
  value,
  onChange,
  label = 'Model Deployment',
  required = false,
  disabled = false,
  error,
  description,
  isCodingAgent = false,
}: DeploymentFieldProps) => {
  const styles = useStyles();

  // Get resourceId from connection references (connectionStore) first,
  // then fall back to workflow store's connections.json for persisted data.
  // This ensures newly created connections work immediately without waiting for save.
  const connectionReferences = useConnectionStore((state) => state.references);
  const workflowConnections = useWorkflowStore((state) => state.connections);
  const agentConnections = workflowConnections?.agentConnections;

  // Detect whether the connection is a GitHub Copilot connection
  const isGhcpConnection = useMemo(() => {
    if (!connectionReferenceName) return false;
    const reference = connectionReferences[connectionReferenceName];
    if (reference) {
      return getAgentReferenceProvider(reference) === 'GitHubCopilot';
    }
    return false;
  }, [connectionReferenceName, connectionReferences]);

  const resourceId = useMemo(() => {
    if (!connectionReferenceName || isGhcpConnection) return undefined;

    // First check connectionStore references (includes newly created connections)
    const reference = connectionReferences[connectionReferenceName];
    if (reference) {
      // resourceId can be in connectionProperties or at root level
      const props = reference.connectionProperties as Record<string, unknown> | undefined;
      if (props?.resourceId && typeof props.resourceId === 'string') {
        return props.resourceId;
      }
      // Also check if resourceId is stored directly on the reference (for legacy format)
      const refAny = reference as unknown as Record<string, unknown>;
      if (refAny.resourceId && typeof refAny.resourceId === 'string') {
        return refAny.resourceId;
      }
    }

    // Fall back to workflow store's agentConnections (persisted data)
    if (agentConnections) {
      const connection = agentConnections[connectionReferenceName];
      if (connection?.resourceId) {
        return connection.resourceId;
      }
    }

    return undefined;
  }, [connectionReferenceName, connectionReferences, agentConnections, isGhcpConnection]);

  // --- ARM Deployments (AzureOpenAI / Foundry) ---
  const {
    deployments,
    isLoading: isLoadingDeployments,
    error: fetchError,
    refetch,
  } = useDeploymentOptions(resourceId ?? '', !!resourceId);

  // --- GitHub Copilot models (dynamic fetch from runtime) ---
  // NOTE: For *coding* agents the GHCP `/models` listing route isn't plumbed
  // through the runtime yet, so the fetch is expected to fail (typically a
  // 403). For those we keep the fetch disabled and let the user type their
  // model name into the freeform combobox below — surfacing a 403 for an
  // expected gap is confusing. Workflow (native) agents keep their original
  // behavior (`isGhcpConnection`). Re-enable for coding agents by dropping the
  // `!isCodingAgent` guard once the runtime models route exists.
  const {
    models: ghcpModels,
    isLoading: isLoadingGhcp,
    error: ghcpFetchError,
    refetch: refetchGhcp,
  } = useGhcpModels(connectionReferenceName ?? '', isGhcpConnection && !isCodingAgent);

  // Unified loading / error state
  const isLoading = isGhcpConnection ? isLoadingGhcp : isLoadingDeployments;
  const activeFetchError = isGhcpConnection ? ghcpFetchError : fetchError;

  // Build options from either source
  const options = useMemo(() => {
    if (isGhcpConnection) {
      return ghcpModels.map((model) => ({
        key: model.id,
        text: model.id,
        subtext: model.name !== model.id ? model.name : '',
      }));
    }
    return deployments.map((deployment) => ({
      key: deployment.name,
      text: deployment.name,
      subtext: `${deployment.modelName} (${deployment.modelVersion})`,
    }));
  }, [isGhcpConnection, ghcpModels, deployments]);

  // Handle selection from dropdown
  const handleSelect = useCallback(
    (_event: unknown, data: { optionValue?: string | undefined }) => {
      const selected = data.optionValue;
      if (selected) {
        if (isGhcpConnection) {
          onChange({ id: '', name: selected, modelName: selected, modelFormat: 'GitHubCopilot', modelVersion: '' });
        } else {
          const deploymentOptionSelected = deployments.find((d) => d.name === selected);
          onChange(deploymentOptionSelected ?? { id: '', name: selected, modelName: '', modelFormat: '', modelVersion: '' });
        }
      }
    },
    [onChange, deployments, isGhcpConnection]
  );

  // Handle manual input in combobox
  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      if (isGhcpConnection) {
        onChange({ id: '', name: inputValue, modelName: inputValue, modelFormat: 'GitHubCopilot', modelVersion: '' });
      } else {
        const deploymentOptionSelected = deployments.find((d) => d.name === inputValue);
        onChange(deploymentOptionSelected ?? { id: '', name: inputValue, modelName: '', modelFormat: '', modelVersion: '' });
      }
    },
    [onChange, deployments, isGhcpConnection]
  );

  const handleRefresh = useCallback(() => {
    if (isGhcpConnection) {
      refetchGhcp();
    } else {
      refetch();
    }
  }, [isGhcpConnection, refetch, refetchGhcp]);

  // For GHCP, field is enabled as long as connection exists; for ARM, requires resourceId
  const isDisabled = disabled || (!isGhcpConnection && !resourceId);
  const validationState = error || activeFetchError ? 'error' : 'none';
  const validationMessage = error ?? activeFetchError?.message ?? null;

  // Determine placeholder text
  const getPlaceholder = () => {
    if (!connectionReferenceName) {
      return 'Configure connection first';
    }
    if (isGhcpConnection) {
      if (isLoading) return 'Loading models...';
      if (ghcpModels.length === 0) return 'Enter model name';
      return 'Select or enter model name';
    }
    if (!resourceId) {
      return 'No resource ID in connection';
    }
    if (isLoading) {
      return 'Loading deployments...';
    }
    if (deployments.length === 0) {
      return 'Enter deployment name';
    }
    return 'Select or enter deployment name';
  };

  return (
    <div className={styles.container}>
      <Field
        label={label}
        required={required}
        validationState={validationState}
        validationMessage={validationMessage}
        {...(description ? { hint: description } : {})}
      >
        <div className={styles.fieldRow}>
          <Combobox
            className={styles.combobox}
            value={value}
            selectedOptions={value ? [value] : []}
            onOptionSelect={handleSelect}
            onInput={handleInput}
            disabled={isDisabled || isLoading}
            placeholder={getPlaceholder()}
            aria-label={label}
            freeform
          >
            {options.map((option) => (
              <Option key={option.key} value={option.key} text={option.text}>
                <div>
                  <Text weight="semibold">{option.text}</Text>
                  {option.subtext && (
                    <>
                      <br />
                      <Text size={200} className={styles.optionSubtext}>
                        {option.subtext}
                      </Text>
                    </>
                  )}
                </div>
              </Option>
            ))}
          </Combobox>
          {(isGhcpConnection || resourceId) && !(isCodingAgent && isGhcpConnection) && (
            <Button
              className={styles.refreshButton}
              appearance="subtle"
              icon={isLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
              onClick={handleRefresh}
              disabled={isLoading || isDisabled}
              aria-label={isGhcpConnection ? 'Refresh models' : 'Refresh deployments'}
              title={isGhcpConnection ? 'Refresh models' : 'Refresh deployments'}
            />
          )}
        </div>
      </Field>
      {!isGhcpConnection && !resourceId && connectionReferenceName && (
        <Text className={styles.hint}>
          Connection does not have a resourceId. Enter the deployment name manually.
        </Text>
      )}

    </div>
  );
};

DeploymentField.displayName = 'DeploymentField';
